// React
import { FunctionComponent, useState } from "react"

// UI Library
import { Button, Form, Modal, Spinner } from "react-bootstrap"

// Local Components
import VehicleIncidentsMiniInfo from "../VehicleIncidentsMiniInfo/VehicleIncidentsMiniInfo"

// Interfaces
import type { I_IncidentAction } from "../../interfaces"

// Services
import VIS from "../../VehicleIncidentsService"
import snackBar from "src/utilities/snackBar/SnackBar"

// CRUD
import patchSafetyEventLabels from "../../crud/PatchSafetyEventLabels"

// Constants
import { SAMSARA_LABEL_TYPES, toLabelPayload } from "../../constants/VehicleSafetyEventLabelTypes"
import vehicleIncidentColorSet from "../../constants/VehicleIncidentColorSet"

// Styles
import "./VehicleIncidentsEditLabels.css"

interface Props {
    incidentAction: I_IncidentAction
}

const formatLabel = (key: string) =>
    key.replace(/([A-Z])/g, " $1").trim().replace(/^./, (c) => c.toUpperCase())

const VehicleIncidentsEditLabels: FunctionComponent<Props> = ({ incidentAction }) => {
    const { retrieveEventFromMap, setIncidentAction, updateEventLabels } = VIS

    const event = retrieveEventFromMap(incidentAction.id || -1)

    const initialLabels = (event?.Label ?? "").split(",").map((l) => l.trim()).filter(Boolean)

    const [selectedLabels, setSelectedLabels] = useState<string[]>(initialLabels)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (!event) return null

    const availableToAdd = SAMSARA_LABEL_TYPES.filter(
        (t) => !selectedLabels.includes(t.label)
    )

    const handleAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value
        if (val && !selectedLabels.includes(val)) {
            setSelectedLabels((prev) => [...prev, val])
        }
        e.target.value = ""
    }

    const handleRemove = (label: string) => {
        setSelectedLabels((prev) => prev.filter((l) => l !== label))
    }

    const handleSave = async () => {
        if (selectedLabels.length === 0) return

        setBusy(true)
        setError(null)

        const payload = selectedLabels.map(toLabelPayload)
        const success = await patchSafetyEventLabels(event.ObjectID, payload)

        if (success) {
            updateEventLabels(event.ObjectID, selectedLabels.join(","))
            setIncidentAction({ action: -1, id: -1 })
            snackBar({ type: "primary", text: "Labels updated.", timeout: 4000 })
        } else {
            setError("Failed to update labels. Please try again.")
        }

        setBusy(false)
    }

    const isEmpty = selectedLabels.length === 0

    return (
        <div className="VehicleIncidentsEditLabels">
            <Modal
                id="vehicle-incidents-edit-labels-modal"
                show
                backdrop="static"
                keyboard
                centered
                className="VehicleIncidentsWorkflowModal"
                animation={false}
            >
                <Modal.Header>
                    <div className="VehicleIncidentsEditLabelsHeader VehicleIncidentsWorkflowModalHeader">
                        <p>Edit Labels</p>
                        {busy && (
                            <Spinner
                                className="VehicleIncidentsWorkflowModalHeaderSpinner"
                                animation="border"
                            />
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <div className="VehicleIncidentsEditLabelsBody">
                        <VehicleIncidentsMiniInfo id={incidentAction.id} />

                        <div className="VehicleIncidentsEditLabelsChips">
                            {selectedLabels.length === 0 && (
                                <span className="VehicleIncidentsEditLabelsEmpty">
                                    No labels selected. At least one is required.
                                </span>
                            )}
                            {selectedLabels.map((label) => (
                                <span key={label} className="VehicleIncidentsEditLabelsChip">
                                    <span
                                        className="VehicleIncidentsEditLabelsChipDot"
                                        style={{ backgroundColor: vehicleIncidentColorSet[label] || "#888" }}
                                    />
                                    {formatLabel(label)}
                                    <button
                                        type="button"
                                        className="VehicleIncidentsEditLabelsChipRemove"
                                        aria-label={`Remove ${formatLabel(label)}`}
                                        onClick={() => handleRemove(label)}
                                        disabled={busy}
                                    >
                                        &times;
                                    </button>
                                </span>
                            ))}
                        </div>

                        <Form.Group className="VehicleIncidentsEditLabelsAddGroup">
                            <Form.Label>Add label</Form.Label>
                            <Form.Control
                                as="select"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleAdd(e)}
                                disabled={busy || availableToAdd.length === 0}
                                defaultValue=""
                            >
                                <option value="" disabled>
                                    {availableToAdd.length === 0 ? "All labels added" : "Select a label to add..."}
                                </option>
                                {availableToAdd.map((t) => (
                                    <option key={t.label} value={t.label}>
                                        {t.name}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {error && (
                            <div className="VehicleIncidentsEditLabelsError">{error}</div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <div className="VehicleIncidentsEditLabelsFooter VehicleIncidentsGenericModalFooter">
                        <div>
                            <Button
                                variant="secondary"
                                onClick={() => setIncidentAction({ action: -1, id: -1 })}
                                disabled={busy}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={busy || isEmpty}
                                title={isEmpty ? "At least one label is required" : undefined}
                            >
                                {busy ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default VehicleIncidentsEditLabels
